import type {
  RunResearchWorkflowRequest,
  RunResearchWorkflowResponse,
  WorldcupResearchCoverageQuery,
  WorldcupResearchCoverageResponse,
  WorldcupResearchExportQuery,
  WorldcupResearchExportResponse,
  WorldcupResearchFacetsQuery,
  WorldcupResearchFacetsResponse,
  WorldcupResearchImportBatchesResponse,
  WorldcupResearchMatchShapesResponse,
  WorldcupResearchProviderAuditResponse,
  WorldcupResearchQuery,
  WorldcupResearchStagePerformanceResponse,
  WorldcupResearchTeamMatchStatsResponse,
  WorldcupResearchTeamPerformanceResponse,
} from './types'
import { authApiClient } from '~/lib/api-client'

const worldcupResearchClient = authApiClient.api.internal['worldcup-research']

function cleanQuery<T extends object>(query: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(query as Record<string, unknown>).filter(([, value]) => value !== undefined && value !== ''),
  ) as Partial<T>
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`
    try {
      const body = await response.json() as {
        error?: { issues?: Array<{ message?: string }>, message?: string }
        message?: string
      }
      message = body.message ?? body.error?.message ?? body.error?.issues?.[0]?.message ?? message
    }
    catch {
      // Keep the status-based fallback when the backend returns a non-JSON error.
    }
    throw new Error(message)
  }
  return response.json() as Promise<T>
}

export async function listTeamMatchStats(filters: WorldcupResearchQuery) {
  const response = await worldcupResearchClient['team-match-stats'].$get({
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchTeamMatchStatsResponse>(response)
}

export async function exportTeamMatchStats(filters: WorldcupResearchExportQuery) {
  const response = await worldcupResearchClient['team-match-stats'].export.$get({
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchExportResponse>(response)
}

export async function listTeamPerformance(filters: WorldcupResearchQuery) {
  const response = await worldcupResearchClient.views['team-performance'].$get({
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchTeamPerformanceResponse>(response)
}

export async function listStagePerformance(filters: WorldcupResearchQuery) {
  const response = await worldcupResearchClient.views['stage-performance'].$get({
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchStagePerformanceResponse>(response)
}

export async function listMatchShapes(filters: WorldcupResearchQuery) {
  const response = await worldcupResearchClient.views['match-shapes'].$get({
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchMatchShapesResponse>(response)
}

export async function listProviderAudit(filters: WorldcupResearchQuery) {
  const response = await worldcupResearchClient['provider-audit'].$get({
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchProviderAuditResponse>(response)
}

export async function listCoverage(filters: WorldcupResearchCoverageQuery) {
  const coverageFilters = {
    season: filters.season,
    competitionCode: filters.competitionCode,
    competitionName: filters.competitionName,
    researchView: filters.researchView,
    minDataQualityScore: filters.minDataQualityScore,
    importBatchId: filters.importBatchId,
    coverageLimit: filters.coverageLimit,
    coverageOffset: filters.coverageOffset,
    gapLimit: filters.gapLimit,
    gapOffset: filters.gapOffset,
  }
  const response = await worldcupResearchClient.coverage.$get({
    query: cleanQuery(coverageFilters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchCoverageResponse>(response)
}

export async function listFacets(filters: WorldcupResearchFacetsQuery) {
  const facetFilters = {
    season: filters.season,
    competitionCode: filters.competitionCode,
    competitionName: filters.competitionName,
    researchView: filters.researchView,
    minDataQualityScore: filters.minDataQualityScore,
    importBatchId: filters.importBatchId,
  }
  const response = await worldcupResearchClient.facets.$get({
    query: cleanQuery(facetFilters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchFacetsResponse>(response)
}

export async function listImportBatches() {
  const response = await worldcupResearchClient['import-batches'].$get({
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchImportBatchesResponse>(response)
}

export async function runResearchWorkflow(request: RunResearchWorkflowRequest) {
  const response = await (worldcupResearchClient.tools as any)['run-research-workflow'].$post({
    json: request,
    header: { Authorization: '' },
  })
  return parseJson<RunResearchWorkflowResponse>(response)
}
